import {
  OrderRequest,
  PlaceOrderAction,
  CancelOrderAction,
  CancelByCloidAction,
  ModifyOrderAction,
  BatchModifyAction,
  UpdateLeverageAction,
  UpdateIsolatedMarginAction,
  TopUpIsolatedMarginAction,
  UsdSendAction,
  SpotSendAction,
  WithdrawAction,
  UsdClassTransferAction,
  SendAssetAction,
  StakingDepositAction,
  StakingWithdrawAction,
  TokenDelegateAction,
  VaultTransferAction,
  ApproveAgentAction,
  ApproveBuilderFeeAction,
  TwapOrderAction,
  TwapCancelAction,
} from '../types/exchange';
import { TimeInForce, ClientOrderId } from '../types/hyperliquid/orders';
import { toHex } from 'viem';
import { defaultChain } from '@/providers/Providers';

export interface BuilderConfig {
  address: string;
  fee: number;
  cloid?: string;
}

/**
 * ActionBuilder is responsible for creating action objects for Hyperliquid exchange
 */
export class ActionBuilder {
  private readonly isMainnet: boolean;
  private readonly builderConfig?: BuilderConfig;

  constructor(builderConfig?: BuilderConfig) {
    this.isMainnet = process.env.NEXT_PUBLIC_NETWORK === 'MAINNET';
    this.builderConfig = builderConfig;
  }

  /**
   * Order Actions
   */
  createLimitOrder(
    asset: number,
    isBuy: boolean,
    price: string,
    size: string,
    reduceOnly: boolean = false,
    tif: TimeInForce = 'Gtc',
    cloid?: ClientOrderId,
  ): OrderRequest {
    const finalCloid = cloid || this.builderConfig?.cloid;

    return {
      a: asset,
      b: isBuy,
      p: price,
      s: size,
      r: reduceOnly,
      t: { limit: { tif } },
      ...(finalCloid && { c: finalCloid }),
    };
  }

  createTriggerOrder(
    asset: number,
    isBuy: boolean,
    triggerPrice: string,
    size: string,
    tpsl: 'tp' | 'sl',
    isMarket: boolean = true,
    reduceOnly: boolean = true,
    cloid?: ClientOrderId,
  ): OrderRequest {
    const finalCloid = cloid || this.builderConfig?.cloid;

    return {
      a: asset,
      b: isBuy,
      p: triggerPrice,
      s: size,
      r: reduceOnly,
      t: {
        trigger: {
          isMarket,
          triggerPx: triggerPrice,
          tpsl,
        },
      },
      ...(finalCloid && { c: finalCloid }),
    };
  }

  placeOrderAction(orders: OrderRequest[], grouping: 'na' | 'normalTpsl' | 'positionTpsl' = 'na'): PlaceOrderAction {
    const ordersWithCloid = this.builderConfig?.cloid
      ? orders.map(order => ({
        ...order,
        c: order.c || this.builderConfig?.cloid,
      }))
      : orders;

    return {
      type: 'order',
      orders: ordersWithCloid,
      grouping,
      ...(this.builderConfig && {
        builder: {
          b: this.builderConfig.address,
          f: this.builderConfig.fee,
        },
      }),
    };
  }

  /**
   * Cancel Actions
   */
  cancelOrderAction(cancels: Array<{ asset: number; oid: number }>): CancelOrderAction {
    return {
      type: 'cancel',
      cancels: cancels.map(c => ({ a: c.asset, o: c.oid })),
    };
  }

  cancelByCloidAction(cancels: Array<{ asset: number; cloid: string }>): CancelByCloidAction {
    return {
      type: 'cancelByCloid',
      cancels,
    };
  }

  scheduleCancelAction(time?: number) {
    return {
      type: 'scheduleCancel' as const,
      ...(time && { time }),
    };
  }

  /**
   * Modify Actions
   */
  modifyOrderAction(oid: number | string, order: OrderRequest): ModifyOrderAction {
    return {
      type: 'modify',
      oid,
      order,
    };
  }

  batchModifyAction(modifies: Array<{ oid: number | string; order: OrderRequest }>): BatchModifyAction {
    return {
      type: 'batchModify',
      modifies,
    };
  }

  /**
   * Leverage & Margin Actions
   */
  updateLeverageAction(asset: number, leverage: number, isCross: boolean = true): UpdateLeverageAction {
    return {
      type: 'updateLeverage',
      asset,
      isCross,
      leverage,
    };
  }

  updateIsolatedMarginAction(asset: number, marginChange: number, isBuy: boolean = true): UpdateIsolatedMarginAction {
    const ntli = Math.floor(marginChange * 1e6);

    return {
      type: 'updateIsolatedMargin',
      asset,
      isBuy,
      ntli,
    };
  }

  topUpIsolatedMarginAction(asset: number, leverage: string): TopUpIsolatedMarginAction {
    return {
      type: 'topUpIsolatedOnlyMargin',
      asset,
      leverage,
    };
  }

  /**
   * Transfer Actions
   */
  usdSendAction(destination: string, amount: string): UsdSendAction {
    return {
      type: 'usdSend',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      destination,
      amount,
      time: Date.now(),
    };
  }

  spotSendAction(destination: string, token: string, amount: string): SpotSendAction {
    return {
      type: 'spotSend',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      destination,
      token,
      amount,
      time: Date.now(),
    };
  }

  withdrawAction(destination: string, amount: string): WithdrawAction {
    return {
      type: 'withdraw3',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      destination,
      amount,
      time: Date.now(),
    };
  }

  usdClassTransferAction(amount: string, toPerp: boolean, subaccount?: string): UsdClassTransferAction {
    return {
      type: 'usdClassTransfer',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      amount,
      toPerp,
      nonce: Date.now(),
      ...(subaccount && { subaccount }),
    };
  }

  sendAssetAction(
    destination: string,
    token: string,
    amount: string,
    sourceDex: string = '',
    destinationDex: string = 'spot',
    fromSubAccount: string = '',
  ): SendAssetAction {
    return {
      type: 'sendAsset',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      destination,
      sourceDex,
      destinationDex,
      token,
      amount,
      fromSubAccount,
      nonce: Date.now(),
    };
  }

  /**
   * Staking Actions
   */
  stakingDepositAction(wei: number): StakingDepositAction {
    return {
      type: 'cDeposit',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      wei,
      nonce: Date.now(),
    };
  }

  stakingWithdrawAction(wei: number): StakingWithdrawAction {
    return {
      type: 'cWithdraw',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      wei,
      nonce: Date.now(),
    };
  }

  tokenDelegateAction(validator: string, wei: number, isUndelegate: boolean = false): TokenDelegateAction {
    return {
      type: 'tokenDelegate',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      validator,
      isUndelegate,
      wei,
      nonce: Date.now(),
    };
  }

  /**
   * Vault Actions
   */
  vaultTransferAction(vaultAddress: string, amount: number, isDeposit: boolean): VaultTransferAction {
    return {
      type: 'vaultTransfer',
      vaultAddress,
      isDeposit,
      usd: amount,
    };
  }

  /**
   * Approval Actions
   */
  approveAgentAction(agentAddress: string, agentName?: string): ApproveAgentAction {
    return {
      type: 'approveAgent',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      agentAddress,
      ...(agentName && { agentName }),
      nonce: Date.now(),
    };
  }

  approveBuilderFeeAction(builder: string, maxFeeRate: string, nonce: number): ApproveBuilderFeeAction {
    return {
      type: 'approveBuilderFee',
      hyperliquidChain: this.isMainnet ? 'Mainnet' : 'Testnet',
      signatureChainId: toHex(defaultChain.id),
      maxFeeRate,
      builder,
      nonce,
    };
  }

  /**
   * TWAP Actions
   */
  twapOrderAction(
    asset: number,
    isBuy: boolean,
    size: string,
    minutes: number,
    randomize: boolean = false,
    reduceOnly: boolean = false,
  ): TwapOrderAction {
    return {
      type: 'twapOrder',
      twap: {
        a: asset,
        b: isBuy,
        s: size,
        r: reduceOnly,
        m: minutes,
        t: randomize,
      },
    };
  }

  twapCancelAction(asset: number, twapId: number): TwapCancelAction {
    return {
      type: 'twapCancel',
      a: asset,
      t: twapId,
    };
  }

  /**
   * Utility Actions
   */
  reserveRequestWeightAction(weight: number) {
    return {
      type: 'reserveRequestWeight' as const,
      weight,
    };
  }

  noopAction() {
    return {
      type: 'noop' as const,
    };
  }
}
